"use server";

// ============================================================
// CLUBHUB – Beiträge speichern (Server Actions)
// ============================================================
// Ohne Sitzung (Demo-Modus, Musterbestand) wird nichts gespeichert,
// die Masken melden nur Erfolg — wie bisher. Mit Sitzung schreiben
// die Actions in den Verein der Sitzung (`requirePermission(
// "finance.write")`), nie in einen aus dem Formular. Die Fachlogik
// steht in `lib/beitraege/schreiben.ts`, die Prüfung der Eingaben in
// `lib/beitraege/eingaben.ts` — dieselben Sätze wie im Dialog.
//
// Mandate (IBAN, Unterschrift, Widerruf, Änderung) stehen in
// `beitraege/mandate/actions.ts`.
//
// Rechnungen erzeugt dieser Bereich NICHT: der Schritt „Rechnungen
// erzeugen und versenden“ im Assistenten bleibt simuliert, bis der
// Belegkern (clubhub-3b) da ist.
// ============================================================
import { revalidatePath } from "next/cache";
import { aktuell } from "@/lib/current";
import type { ActionResult } from "@/lib/action-result";
import { verlangeBestaetigung } from "@/lib/auth/bestaetigung";
import { pruefeBeitragsart, pruefeLaufEinstellung, pruefeZuordnung } from "@/lib/beitraege/eingaben";
import * as schreiben from "@/lib/beitraege/schreiben";

async function schreibZugang() {
  const { requirePermission, Forbidden } = await import("@/lib/tenant");
  try {
    const ctx = await requirePermission("finance.write");
    const { db } = await import("@/lib/db");
    return { ok: true as const, db, wer: { clubId: ctx.clubId, userId: ctx.userId } };
  } catch (e) {
    if (e instanceof Forbidden) return { ok: false as const, fehler: "Dafür fehlt dir das Recht, Beiträge zu bearbeiten." };
    throw e;
  }
}

async function imMuster(): Promise<boolean> {
  return (await aktuell()).quelle === "muster";
}

function neuLaden() {
  revalidatePath("/beitraege", "layout");
  revalidatePath("/mitglieder", "layout");
}

// ── Beitragsarten ───────────────────────────────────────────

export async function beitragsartSpeichern(id: string | null, eingabe: unknown): Promise<ActionResult<{ id: string }>> {
  const p = pruefeBeitragsart(eingabe);
  if (!p.ok) return { error: p.error, field: p.field };
  if (await imMuster()) return { success: true, id: id ?? "f-neu" };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const r = await schreiben.beitragsartSpeichern(z.db, z.wer, id, p.wert);
  if (r.success) neuLaden();
  return r;
}

export async function beitragsartAktivSetzen(id: string, aktiv: boolean): Promise<ActionResult> {
  if (await imMuster()) return { success: true };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const r = await schreiben.beitragsartAktivSetzen(z.db, z.wer, String(id), aktiv === true);
  if (r.success) neuLaden();
  return r;
}

export async function beitragsartLoeschen(id: string): Promise<ActionResult> {
  if (await imMuster()) return { success: true };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const r = await schreiben.beitragsartLoeschen(z.db, z.wer, String(id));
  if (r.success) neuLaden();
  return r;
}

// ── Zuordnungen ─────────────────────────────────────────────

export async function zuordnungSpeichern(id: string | null, eingabe: unknown): Promise<ActionResult<{ id: string }>> {
  const p = pruefeZuordnung(eingabe);
  if (!p.ok) return { error: p.error, field: p.field };
  if (await imMuster()) return { success: true, id: id ?? "cf-neu" };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const r = await schreiben.zuordnungSpeichern(z.db, z.wer, id, p.wert);
  if (r.success) neuLaden();
  return r;
}

// ── Beitragslauf ────────────────────────────────────────────

/** Eckdaten als Entwurf (BillingRun DRAFT) — noch keine Rechnungen. */
export async function laufEntwurfSpeichern(id: string | null, einstellung: unknown): Promise<ActionResult<{ id: string | null }>> {
  const p = pruefeLaufEinstellung(einstellung);
  if (!p.ok) return { error: p.error, field: p.field };
  if (await imMuster()) return { success: true, id: null };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const r = await schreiben.laufEntwurfSpeichern(z.db, z.wer, id, p.wert);
  if (r.success) revalidatePath("/beitraege", "layout");
  return r;
}

export async function laufEntwurfVerwerfen(id: string): Promise<ActionResult> {
  if (await imMuster()) return { success: true };
  const z = await schreibZugang();
  if (!z.ok) return { error: z.fehler };
  const r = await schreiben.laufEntwurfVerwerfen(z.db, z.wer, String(id));
  if (r.success) revalidatePath("/beitraege", "layout");
  return r;
}

/**
 * Die SEPA-Datei anfordern: Recht und Bestätigung prüft der Server
 * (heikle Aktion „sepa.datei“). Die Datei selbst entsteht erst mit den
 * Rechnungen des Belegkerns — bis dahin meldet die Action nur, dass sie
 * erlaubt wäre (`simuliert`).
 */
export async function sepaDateiAnfordern(): Promise<ActionResult<{ simuliert: true }>> {
  if (!(await imMuster())) {
    const z = await schreibZugang();
    if (!z.ok) return { error: z.fehler };
  }
  const halt = await verlangeBestaetigung("sepa.datei");
  if (halt) return halt;
  return { success: true, simuliert: true };
}
